import { EN_KO } from './_EN_KO.js';
import { PromiseWithResolvers, yieldToMain } from './Promise';
import TaoConsole from './TaoConsole';

/**
 * Function to handle execution of a callback function once the document is ready or when a custom deferred
 * execution condition is met within the `EN_KO` namespace. Provides a fallback mechanism using
 * `DOMContentLoaded` in absence of the `EN_KO.deferredExecute` setup.
 *
 * @param {string}   name - The name associated with the initialization process, typically used for debugging or logging purposes.
 * @param {Function} func - The callback function to execute once the readiness condition is fulfilled. It supports asynchronous operations.
 * @return {Promise} - A promise that resolves when the readiness conditions are met and the callback has been executed.
 */
const ready = function( name, func ) {
	const promise = PromiseWithResolvers();

	if ( 'deferredExecute' in EN_KO ) {
		EN_KO.deferredExecute.push( () => promise.resolve( true ) );
	} else {
		// Native Fallback in case index.js is not loading.
		if ( document.readyState === 'loading' ) { // eslint-disable-line no-lonely-if
			document.addEventListener( 'DOMContentLoaded', () => promise.resolve( true ) );
		} else {
			promise.resolve( true );
		}
	}

	return promise.promise.then( async() => {
		await yieldToMain();
		return await func();
	} ).catch( ( errorMessage ) => {
		// eslint-disable-next-line no-console
		TaoConsole.error( `Error initialising ${ name }!`, errorMessage );
		return Promise.reject( errorMessage );
	} );
};

/**
 *  An array intended to hold a sequence of promises in a chain.
 *
 * @type {Array}
 */
const promiseChain = [];

/**
 * Adds a function to the promise chain with a specified name and priority.
 *
 * The `chainReady` function allows for queueing asynchronous functions to execute
 * in a specific order defined by their priority. Each function is wrapped in a promise
 * to ensure proper resolution and execution sequence.
 *
 * @param {string}   name     - The name of the function to be added to the chain.
 * @param {Function} fn       - The asynchronous function to be executed. This function
 *                            accepts two parameters: `resolve` and `reject`, to control
 *                            the resolution of the generated promise.
 * @param {number}   priority - The priority of the function in the chain. Lower values
 *                            indicate higher priority, and functions are executed in
 *                            ascending order of priority.
 */
const chainReady = function( name, fn, priority ) {
	promiseChain.push( {
		name,
		func() {
			return new Promise( async function( resolve, reject ) {
				await yieldToMain();
				return await fn( resolve, reject );
			} );
		},
		priority,
	} );

	promiseChain.sort( function( a, b ) {
		return a.priority - b.priority;
	} );
};

/**
 * Execute the Promises in the PromiseChain on after the other.
 */
void ready( 'Promises', function() {
	// Run all chained inits, ordered by priority.
	return promiseChain.reduce( function( accumulator, chainItem ) {
		return accumulator.then( chainItem.func );
	}, Promise.resolve() );
} );

const TaoReady = {
	ready,
	promiseChain,
	chainReady,
};

export default TaoReady;
